// src/components/Services/modals/ServiceEditModal.tsx
import React, { useState, useEffect, useCallback, useId, useRef, useMemo } from "react";
import { Calendar, Users, ClipboardList, ChevronDown, UserPlus } from "lucide-react";
import BaseButton from "../../Base/BaseButton";
import AccessibleDatePicker from "./AccessibleDatePicker";
import AccessibleTimePicker from "./AccessibleTimePicker";
import BaseSelect from "../../Base/BaseSelect";
import MultiSelectSearchable from "../../Base/MultiSelectSearchable";
import showToast from "../../../utils/toast";

import useServicesService from "../../../services/useServicesService";
import {
  PassengerEdit,
  ServiceEdit,
  ServiceEditApiPayload,
} from "../../../models/ServiceModel";
import { ServicePriority, ServiceStatus } from "../../../enums/ServiceEnum";

import "../styles/ServiceEditModal.css";
import PassengerServicesTable from "../tables/Services/PassengerServicesTable";

type EditSectionKey = "general" | "assignments" | "passengers";

type AccordionSectionProps = {
  sectionKey: EditSectionKey;
  title: React.ReactNode;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

const EditAccordionSection: React.FC<AccordionSectionProps> = ({
  sectionKey,
  title,
  icon,
  badge,
  isOpen,
  onToggle,
  children,
}) => {
  const uid = useId();
  const baseId = `svc-edit-${sectionKey}-${uid.replace(/:/g, "")}`;
  const triggerId = `${baseId}-trigger`;
  const panelId = `${baseId}-panel`;

  return (
    <section className="svc-edit-accordion" data-section={sectionKey}>
      <button
        type="button"
        id={triggerId}
        className="svc-edit-accordion__trigger"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="svc-edit-accordion__trigger-main">
          <span className="svc-edit-accordion__icon-wrap" aria-hidden>
            {icon}
          </span>
          <span className="svc-edit-accordion__title">{title}</span>
          {badge ? <span className="svc-edit-accordion__badge">{badge}</span> : null}
        </span>
        <ChevronDown
          className={`svc-edit-accordion__chevron ${isOpen ? "is-open" : ""}`}
          size={22}
          strokeWidth={2.25}
          aria-hidden
        />
      </button>
      {isOpen ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          className="svc-edit-accordion__panel"
        >
          {children}
        </div>
      ) : null}
    </section>
  );
};

interface Props {
  serviceId: number;
  /** Si el padre ya cargó GET services/edit (p. ej. ServiceEditPage), se reutiliza y no se hace otro GET. */
  initialEditData?: ServiceEditApiPayload | null;
  onClose: () => void;
  onUpdated: () => void;
  onError?: (error: string) => void;
  passengers: any[];
  fieldError?: string | null;
  onAddSequence: (seq: any[]) => void;
  initialSection?: EditSectionKey;
  initialPassengerId?: number | null;
}

const STATUS_OPTIONS: { value: ServiceStatus; label: string }[] = [
  { value: ServiceStatus.PREPARACION, label: "Preparación" },
  { value: ServiceStatus.AUTORIZADO, label: "Autorizado" },
  { value: ServiceStatus.ASIGNADO, label: "Asignado" },
  { value: ServiceStatus.EN_CURSO, label: "En curso" },
  { value: ServiceStatus.COMPLETADO, label: "Completado" },
  { value: ServiceStatus.CANCELADO, label: "Cancelado" },
  { value: ServiceStatus.ANULADO, label: "Anulado" },
];

const MAX_PASSENGERS = 7;

const normalizeUiError = (value: any): string => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(normalizeUiError).filter(Boolean).join(" ");
  if (value && typeof value === "object") {
    const first = Object.values(value).map(normalizeUiError).find(Boolean);
    return first || "Error del servidor";
  }
  return "Error del servidor";
};

const ServiceEditModal: React.FC<Props> = ({
  serviceId,
  initialEditData = null,
  onClose,
  onUpdated,
  onError,
  fieldError,
  onAddSequence,
  initialSection,
  initialPassengerId,
}) => {
  const { edit, update } = useServicesService();
  const didHydrateRef = useRef(false);

  const [form, setForm] = useState<ServiceEdit | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [availableAuthorizers, setAvailableAuthorizers] = useState<any[]>([]);
  const [availableRequesters, setAvailableRequesters] = useState<any[]>([]);
  const [availableCostCenters, setAvailableCostCenters] = useState<any[]>([]);
  const [selectedPassengerIds, setSelectedPassengerIds] = useState<number[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [availableAddresses, setAvailableAddresses] = useState<{ id: number; street: string }[]>([]);
  const [passengerOptions, setPassengerOptions] = useState<any[]>([]);
  
  const [rows, setRows] = useState<any[]>([]);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [scheduleComment, setScheduleComment] = useState<string>("");
  const [scheduleMedium, setScheduleMedium] = useState<"" | "correo" | "llamada" | "otros">("");
  const [originalSchedule, setOriginalSchedule] = useState<{ date: string; time: string } | null>(null);
  const [openSections, setOpenSections] = useState<Record<EditSectionKey, boolean>>(() => ({
    general: initialSection === "general",
    assignments: initialSection === "assignments",
    passengers: initialSection === "passengers",
  }));

  const normalizeTime = (value: string | null | undefined) =>
    String(value ?? "").trim().slice(0, 5);

  const scheduleChanged = Boolean(
    form &&
      originalSchedule &&
      (String(form.service_date ?? "").trim() !== String(originalSchedule.date ?? "").trim() ||
        normalizeTime(form.service_time) !== normalizeTime(originalSchedule.time))
  );

  const toggleSection = (key: EditSectionKey) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const availableSlots = Math.max(0, MAX_PASSENGERS - rows.length);
  const isManualService = form?.service_type === "MANUAL";
  const singleSelectedPassengerId =
    selectedPassengerIds.length === 1 ? selectedPassengerIds[0] : null;

  const passengerSelectOptions = useMemo(() => {
    const addedIds = new Set(rows.map((row) => row.id));
    return passengerOptions
      .filter((passenger) => !addedIds.has(passenger.id))
      .map((passenger) => ({
        value: String(passenger.id),
        label: `${passenger.name ?? ""} ${passenger.last_name ?? ""}`.trim(),
      }));
  }, [passengerOptions, rows]);

  const handlePassengerChange = (ids: number[]) => {
    const nextIds = ids.slice(0, availableSlots);
    setSelectedPassengerIds(nextIds);
    setSelectedAddressId(null);

    if (nextIds.length === 1) {
      const passenger = passengerOptions.find((p) => p.id === nextIds[0]);
      setAvailableAddresses(passenger?.addresses ?? []);
    } else {
      setAvailableAddresses([]);
    }
  };

  const handleAddPassenger = () => {
    if (selectedPassengerIds.length === 0) return;

    const updatedRows = [...rows];
    let added = 0;
    let skippedNoAddress = 0;

    for (const passengerId of selectedPassengerIds) {
      if (updatedRows.length >= MAX_PASSENGERS) break;

      const passenger = passengerOptions.find((p) => p.id === passengerId);
      if (!passenger || updatedRows.some((row) => row.id === passenger.id)) continue;

      let address = "MANUAL";
      let addressId: number | null = null;
      if (!isManualService) {
        const addresses = passenger.addresses ?? [];
        const selectedAddress =
          passengerId === singleSelectedPassengerId && selectedAddressId != null
            ? addresses.find((item: any) => item.id === selectedAddressId)
            : addresses[0];
        if (!selectedAddress) {
          skippedNoAddress += 1;
          continue;
        }
        address = selectedAddress.street;
        addressId = selectedAddress.id;
      }

      updatedRows.push({
        id: passenger.id,
        name: `${passenger.name ?? ""} ${passenger.last_name ?? ""}`.trim(),
        phone: passenger.phone ?? "",
        address,
        address_id: addressId,
        order: updatedRows.length + 1,
      });
      added += 1;
    }

    if (added === 0) {
      showToast(
        skippedNoAddress > 0
          ? "Algunos pasajeros no tienen dirección registrada"
          : "No se agregaron pasajeros nuevos",
        "info"
      );
      return;
    }

    setRows(updatedRows);
    setForm((prev) => ({ ...prev!, passengers: updatedRows }));
    onAddSequence(updatedRows);

    setSelectedPassengerIds([]);
    setSelectedAddressId(null);
    setAvailableAddresses([]);

    if (skippedNoAddress > 0) {
      showToast(`${added} agregado(s); ${skippedNoAddress} sin dirección`, "info");
    }
  };

  const handleQuickEditPassenger = (index: number, updatedRow: any) => {
    const updatedRows = rows.map((row, i) => (i === index ? { ...row, ...updatedRow } : row));
    updatedRows.forEach((p, i) => (p.order = i + 1));
    setRows(updatedRows);
    setForm((prev) => ({ ...prev!, passengers: updatedRows }));
    onAddSequence(updatedRows);
    showToast("Pasajero actualizado", "success");
  };

  const forceEditIndex = useMemo(() => {
    if (!initialPassengerId) return null;
    const idx = rows.findIndex((r) => Number(r.id) === Number(initialPassengerId));
    return idx >= 0 ? idx : null;
  }, [rows, initialPassengerId]);
  const applyEditApiData = useCallback((data: ServiceEditApiPayload) => {
    const serviceData = data.service ?? {};

    const passengers = Array.isArray(data.passengers)
      ? data.passengers.map((p: any, i: number) => ({
          id: p.id,
          name: p.name,
          phone: p.phone,
          address: p.address,
          address_id: p.address_id ?? null,
          order: p.order ?? i + 1,
        }))
      : [];
    setPassengerOptions(data.passenger_options ?? []);
    setForm({ ...serviceData, passengers } as ServiceEdit);
    setLoading(false);
    setRows(passengers);
    setCancelReason("");
    setScheduleComment("");
    setScheduleMedium("");
    setOriginalSchedule({
      date: String(serviceData.service_date ?? "").trim(),
      time: String(serviceData.service_time ?? "").trim(),
    });

    const selects = data.form_options?.assignment_selects;
    if (selects) {
      setAvailableDrivers(selects.drivers ?? []);
      setAvailableAuthorizers(selects.authorizers ?? []);
      setAvailableRequesters(selects.requesters ?? []);
      setAvailableCostCenters(selects.cost_centers ?? []);
    } else {
      setAvailableDrivers([]);
      setAvailableAuthorizers([]);
      setAvailableRequesters([]);
      setAvailableCostCenters([]);
    }
  }, []);

  // === Cargar datos iniciales (solo si no hay payload del padre) ===
  const ReloadForm = useCallback(() => {
    setLoading(true);
    edit(serviceId, {
      onSuccess: (resp) => {
        applyEditApiData((resp?.data ?? {}) as ServiceEditApiPayload);
      },
      onError: () => {
        showToast("Error cargando servicio", "error");
        setLoading(false);
      },
    });
  }, [applyEditApiData, edit, serviceId]);

  const handleAddressChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const addressId = e.target.value ? Number(e.target.value) : null;
    setSelectedAddressId(addressId);

    // Opcional: puedes guardar la dirección seleccionada en el form para futuras validaciones o debug
    if (addressId) {
      const selectedAddress = availableAddresses.find((a) => a.id === addressId);
      if (selectedAddress) {
        setForm((prev) => ({
          ...prev!,
          last_selected_address: {
            id: selectedAddress.id,
            street: selectedAddress.street,
          },
        }));
      }
    }
  };
  useEffect(() => {
    if (didHydrateRef.current) return;

    const sid = Number(serviceId);
    const payloadSid = Number(initialEditData?.service?.id);
    if (
      initialEditData &&
      initialEditData.service &&
      !Number.isNaN(payloadSid) &&
      payloadSid === sid
    ) {
      didHydrateRef.current = true;
      applyEditApiData(initialEditData);
      return;
    }

    didHydrateRef.current = true;
    ReloadForm();
  }, [applyEditApiData, initialEditData, ReloadForm, serviceId]);

  // === Handlers de selects específicos ===
  const handleDriverChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value ? parseInt(e.target.value) : null;
    setForm((prev) => ({ ...prev!, driver: id }));
  };

  const handleAuthorizerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value ? parseInt(e.target.value) : null;
    setForm((prev) => ({ ...prev!, authorizer: id }));
  };

  const handleRequesterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value ? parseInt(e.target.value) : null;
    setForm((prev) => ({ ...prev!, requester: id }));
  };

  const handleCostCenterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value ? parseInt(e.target.value) : null;
    setForm((prev) => ({ ...prev!, cost_center: id }));
  };

  // === Guardar ===
  const handleSubmit = async () => {
    if (!form) return;

    const requiresNote = [ServiceStatus.ANULADO, ServiceStatus.CANCELADO].includes(form.status);
    if (requiresNote && !cancelReason?.trim()) {
      showToast("Debe agregar una nota obligatoria para anular o cancelar el servicio.", "error");
      if (onError) onError("Debe agregar una nota obligatoria para anular o cancelar el servicio.");
      return;
    }

    if (scheduleChanged) {
      if (!scheduleMedium) {
        showToast("Debes indicar el medio del cambio (correo, llamada u otros).", "error");
        if (onError) onError("Debes indicar el medio del cambio (correo, llamada u otros).");
        return;
      }
      if (!scheduleComment.trim()) {
        showToast("Debes ingresar un comentario para modificar el horario del servicio.", "error");
        if (onError) onError("Debes ingresar un comentario para modificar el horario del servicio.");
        return;
      }
    }

    setSaving(true);

    // Asegurar que los pasajeros tengan address_id y orden correcto
    const payload = {
      ...form,
      cancel_reason: requiresNote ? cancelReason?.trim() || undefined : undefined,
      schedule_medium: scheduleChanged ? scheduleMedium : undefined,
      schedule_comment: scheduleChanged ? scheduleComment.trim() : undefined,
      passengers: form.passengers.map((p: any, index: number) => {
        // Asegurar que el orden sea consecutivo (por si acaso)
        const passenger = {
          id: p.id,
          name: p.name,
          phone: p.phone,
          address: p.address,
          address_id: p.address_id ?? null,
          order: p.order ?? (index + 1), // Usar order del pasajero o index + 1 como fallback
        };
        return passenger;
      }),
    };

    update(serviceId, payload, {
      onSuccess: (resp) => {
        const responseData = resp?.data ?? {};
        if (responseData.passengers && Array.isArray(responseData.passengers)) {
          const updatedPassengers = responseData.passengers.map((p: any, i: number) => ({
            id: p.id,
            name: p.name,
            phone: p.phone,
            address: p.address,
            address_id: p.address_id ?? null,
            order: p.order ?? i + 1,
          }));
          setRows(updatedPassengers);
          setForm((prev) => ({ ...prev!, passengers: updatedPassengers }));
        }
        
        showToast("Servicio actualizado correctamente", "success");
        setSaving(false);
        
        // Llamar al callback para recargar en la página principal
        onUpdated();
        
        // Cerrar el modal después de un pequeño delay para que se vea el mensaje
        setTimeout(() => {
          onClose();
        }, 100);
      },
      onError: (err) => {
        const msg = normalizeUiError(err?.errors || err?.message || err?.data?.message);
        // #region agent log
        fetch('http://127.0.0.1:7537/ingest/2f8325d4-d0b4-41b2-adba-168b860c7d59',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'1748a9'},body:JSON.stringify({sessionId:'1748a9',runId:'pre-fix',hypothesisId:'H2',location:'frontend/src/components/Services/forms/ServiceEditModal.tsx:349',message:'Modal onError message composition',data:{errType:typeof err,errorsType:typeof err?.errors,messageType:typeof err?.message,computedMsgType:typeof msg,computedMsg:msg},timestamp:Date.now()})}).catch(()=>{});
        // #endregion
        showToast(msg, "error");
        setSaving(false);
        if (onError) onError(msg);
      },
    });
  };

  if (loading || !form) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-content-loading" onClick={(e) => e.stopPropagation()}>
          <div className="loading-state">
            <div className="loading-spinner" aria-hidden="true" />
            <p className="loading-text">Cargando datos del servicio…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="service-header">
        <div className="service-header-left">
          <button className="btn-back" onClick={onClose}>← Volver</button>
        </div>

        <div className="service-header-right">
          <h2 className="service-number">Editar Servicio {serviceId}</h2>
        </div>
      </div>

        <div className="modal-body">
          {fieldError && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠</span>
              <span>{fieldError}</span>
            </div>
          )}

          <EditAccordionSection
            sectionKey="general"
            isOpen={openSections.general}
            onToggle={() => toggleSection("general")}
            icon={<Calendar size={20} strokeWidth={2} />}
            title="Información general"
          >
            <div className="svc-edit-accordion__grid svc-edit-modal__datetime-grid">
              <div className="form-field svc-edit-modal__field svc-edit-modal__field--date">
                <AccessibleDatePicker
                  id="svc-edit-service_date"
                  name="service_date"
                  label="Fecha *"
                  value={form.service_date ?? ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev!,
                      service_date: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="form-field svc-edit-modal__field svc-edit-modal__field--time">
                <AccessibleTimePicker
                  id="svc-edit-service_time"
                  name="service_time"
                  label="Hora *"
                  value={form.service_time ?? ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev!,
                      service_time: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="form-field svc-edit-modal__field svc-edit-modal__field--priority">
                <BaseSelect
                  label="Prioridad"
                  name="priority"
                  preserveCase
                  placeholder="Prioridad"
                  options={[
                    { value: ServicePriority.NORMAL, label: "Normal" },
                    { value: ServicePriority.VIP, label: "VIP" },
                  ]}
                  value={String(form.priority ?? ServicePriority.NORMAL)}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev!,
                      priority: (e.target.value || ServicePriority.NORMAL) as ServicePriority,
                    }))
                  }
                />
              </div>
              <div className="form-field svc-edit-modal__field svc-edit-modal__field--purple">
                <label className="svc-edit-purple-toggle">
                  <input
                    type="checkbox"
                    name="style_color"
                    checked={form.style_color === "purple"}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev!,
                        style_color: e.target.checked ? "purple" : null,
                      }))
                    }
                  />
                  <span className="svc-edit-purple-toggle__title">Color morado</span>
                </label>
              </div>
            </div>

            {scheduleChanged && (
              <div className="form-group form-group-full" style={{ marginTop: 12 }}>
                <BaseSelect
                  label="Medio del cambio *"
                  name="schedule_medium"
                  preserveCase
                  placeholder="Seleccione medio"
                  options={[
                    { value: "correo", label: "Correo" },
                    { value: "llamada", label: "Llamada" },
                    { value: "otros", label: "Otros" },
                  ]}
                  value={scheduleMedium}
                  onChange={(e) =>
                    setScheduleMedium((e.target.value || "") as "" | "correo" | "llamada" | "otros")
                  }
                />
                <label
                  className="form-label form-label-required"
                  htmlFor="svc-edit-schedule_comment"
                  style={{ marginTop: 12 }}
                >
                  Comentario obligatorio (Cambio Horario)
                </label>
                <textarea
                  id="svc-edit-schedule_comment"
                  className="form-textarea"
                  value={scheduleComment}
                  onChange={(e) => setScheduleComment(e.target.value)}
                  placeholder="Ej: cambio autorizado por Juan Pérez"
                  rows={3}
                  required
                />
              </div>
            )}
          </EditAccordionSection>

          <EditAccordionSection
            sectionKey="assignments"
            isOpen={openSections.assignments}
            onToggle={() => toggleSection("assignments")}
            icon={<ClipboardList size={20} strokeWidth={2} />}
            title="Asignaciones"
          >
            <div className="form-grid svc-edit-accordion__grid">
              <BaseSelect
                label="Conductor *"
                name="driver"
                placeholder="Seleccione conductor"
                preserveCase
                options={availableDrivers.map((d) => ({
                  value: String(d.value),
                  label: d.label,
                }))}
                value={String(form.driver ?? "")}
                onChange={handleDriverChange}
              />

              <BaseSelect
                label="Autorizador"
                name="authorizer"
                placeholder={
                  availableAuthorizers.length > 0
                    ? "Seleccionar autorizador"
                    : "Sin autorizadores disponibles"
                }
                preserveCase
                options={availableAuthorizers.map((a) => ({
                  value: String(a.value),
                  label: a.label,
                }))}
                value={String(form.authorizer ?? "")}
                onChange={handleAuthorizerChange}
              />

              <BaseSelect
                label="Solicitante"
                name="requester"
                placeholder={
                  availableRequesters.length > 0
                    ? "Seleccionar solicitante"
                    : "Sin solicitantes disponibles"
                }
                preserveCase
                options={availableRequesters.map((r) => ({
                  value: String(r.value),
                  label: r.label,
                }))}
                value={String(form.requester ?? "")}
                onChange={handleRequesterChange}
              />

              <BaseSelect
                label="Centro de costo"
                name="cost_center"
                placeholder={
                  availableCostCenters.length > 0
                    ? "Seleccionar centro de costo"
                    : "Sin centros disponibles"
                }
                preserveCase
                options={availableCostCenters.map((c) => ({
                  value: String(c.value),
                  label: c.label,
                }))}
                value={String(form.cost_center ?? "")}
                onChange={handleCostCenterChange}
              />

              <div className="svc-edit-modal__field svc-edit-modal__field--status">
                <BaseSelect
                  label="Estado del servicio *"
                  name="status"
                  placeholder="Seleccionar estado"
                  options={STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                  value={String(form.status ?? "")}
                  onChange={(e) => {
                    const newStatus = e.target.value as ServiceStatus;
                    setForm((prev) => ({ ...prev!, status: newStatus }));
                    if (![ServiceStatus.ANULADO, ServiceStatus.CANCELADO].includes(newStatus)) {
                      setCancelReason("");
                    }
                  }}
                />
              </div>

              {[ServiceStatus.ANULADO, ServiceStatus.CANCELADO].includes(form.status) && (
                <div className="form-group form-group-full">
                  <label className="form-label form-label-required">
                    Nota obligatoria ({form.status === ServiceStatus.ANULADO ? "anulación" : "cancelación"})
                  </label>
                  <textarea
                    className="form-textarea"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Indique el motivo de la anulación o cancelación..."
                    rows={3}
                    required
                  />
                </div>
              )}
            </div>
          </EditAccordionSection>

          <EditAccordionSection
            sectionKey="passengers"
            isOpen={openSections.passengers}
            onToggle={() => toggleSection("passengers")}
            icon={<Users size={20} strokeWidth={2} />}
            title="Pasajeros"
            badge={
              <span className="svc-edit-accordion__count">
                {rows.length} {rows.length === 1 ? "pasajero" : "pasajeros"}
              </span>
            }
          >
            <div className="form-section-passengers svc-edit-passengers-inner">
              <div className="passenger-add-block">
                <div className="passenger-add-row">
                  <MultiSelectSearchable
                    label="Pasajero"
                    name="selected_passenger"
                    placeholder="Toca para buscar y marcar (1 a 7)"
                    searchPlaceholder="Buscar por nombre…"
                    preserveCase
                    sortAlphabetically
                    selectedFirst
                    menuPortal
                    maxSelections={availableSlots}
                    onMaxSelections={() =>
                      showToast(
                        availableSlots === 0
                          ? `La tabla ya alcanzó el máximo de ${MAX_PASSENGERS}`
                          : `Solo puedes seleccionar ${availableSlots} pasajero(s) más`,
                        "info"
                      )
                    }
                    options={passengerSelectOptions}
                    value={selectedPassengerIds}
                    onChange={handlePassengerChange}
                  />

                  {!isManualService && singleSelectedPassengerId != null && (
                  <div className="form-group address-select-wrap">
                    <BaseSelect
                      label="Dirección"
                      name="address"
                      placeholder={
                        availableAddresses.length > 0
                          ? "Seleccione dirección"
                          : "Sin direcciones"
                      }
                      preserveCase
                      options={availableAddresses.map((a) => ({
                        value: String(a.id),
                        label: a.street,
                      }))}
                      value={selectedAddressId ? String(selectedAddressId) : ""}
                      onChange={handleAddressChange}
                      disabled={availableAddresses.length === 0}
                    />
                  </div>
                  )}
                </div>
                <div className="passenger-add-actions">
                  <BaseButton
                    label={
                      selectedPassengerIds.length > 1
                        ? `Agregar ${selectedPassengerIds.length} pasajeros`
                        : "Agregar pasajero"
                    }
                    leftIcon={<UserPlus size={18} strokeWidth={2.25} aria-hidden />}
                    variant="primary"
                    className="svc-btn-add svc-btn-add--primary"
                    onClick={handleAddPassenger}
                    disabled={
                      selectedPassengerIds.length === 0 ||
                      (!isManualService &&
                        selectedPassengerIds.some((id) => {
                          const passenger = passengerOptions.find((p) => p.id === id);
                          return (passenger?.addresses ?? []).length === 0;
                        }))
                    }
                  />
                </div>
              </div>

              <PassengerServicesTable
                title="Listado de pasajeros"
                rows={rows}
                forceEditIndex={forceEditIndex}
                onQuickEdit={handleQuickEditPassenger}
                onRemove={(index) => {
                  const updatedRows = rows.filter((_, i) => i !== index);
                  updatedRows.forEach((p, i) => (p.order = i + 1));
                  setRows(updatedRows);
                  setForm((prev) => ({ ...prev!, passengers: updatedRows }));
                  onAddSequence(updatedRows);
                }}
                passengers={rows}
                onMove={(from, to) => {
                  const reordered = [...rows];
                  const [moved] = reordered.splice(from, 1);
                  reordered.splice(to, 0, moved);
                  reordered.forEach((p, i) => (p.order = i + 1));
                  setRows(reordered);
                  setForm((prev) => ({ ...prev!, passengers: reordered }));
                  onAddSequence(reordered);
                }}
              />
            </div>
          </EditAccordionSection>

        </div>

        <div className="modal-footer">
          <BaseButton label="Cancelar" onClick={onClose} disabled={saving} aria-label="Cancelar y volver sin guardar" />
          <BaseButton
            label={saving ? "Guardando..." : "Guardar cambios"}
            variant="primary"
            onClick={handleSubmit}
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
};

export default ServiceEditModal;
